import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TypeBadge } from '@/components/ui/type-badge';
import { SectorBadge } from '@/components/ui/sector-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Building2, Handshake, TrendingUp, ArrowRight, GraduationCap, 
  FileCheck, DollarSign, BarChart, Users, Award, 
  MapPin, Target, CheckCircle, Calendar
} from 'lucide-react';

interface Opportunity {
  id: string;
  title: string;
  type: 'job' | 'tender' | 'jv' | 'training' | 'service_offer';
  sector: 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';
  country: string;
  city: string | null;
  spa_linked: boolean;
  company: { name: string };
}

interface Program {
  id: string;
  title: string;
  description: string;
  spa_pillar: string | null;
  deadline: string | null;
}

const ForBusinesses = () => {
  const { t } = useTranslation();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      supabase
        .from('opportunities')
        .select('id, title, type, sector, country, city, spa_linked, company:companies(name)')
        .in('type', ['tender', 'jv', 'service_offer'])
        .eq('status', 'open')
        .order('published_at', { ascending: false })
        .limit(6),
      supabase
        .from('spa_programs')
        .select('id, title, description, spa_pillar, deadline')
        .limit(4)
    ]).then(([oppsRes, programsRes]) => {
      setOpportunities((oppsRes.data as unknown as Opportunity[]) || []);
      setPrograms((programsRes.data as unknown as Program[]) || []);
      setLoading(false);
    });
  }, []);

  const benefits = [
    {
      icon: Target,
      title: t('forBusinesses.benefits.contracts.title'),
      description: t('forBusinesses.benefits.contracts.description'),
      color: 'bg-primary/10 text-primary'
    },
    {
      icon: Handshake,
      title: t('forBusinesses.benefits.partnerships.title'),
      description: t('forBusinesses.benefits.partnerships.description'),
      color: 'bg-secondary/10 text-secondary'
    },
    {
      icon: GraduationCap,
      title: t('forBusinesses.benefits.capacity.title'),
      description: t('forBusinesses.benefits.capacity.description'),
      color: 'bg-accent/10 text-accent-foreground'
    },
    {
      icon: TrendingUp,
      title: t('forBusinesses.benefits.export.title'),
      description: t('forBusinesses.benefits.export.description'),
      color: 'bg-muted text-muted-foreground'
    },
    {
      icon: DollarSign,
      title: t('forBusinesses.benefits.financial.title'),
      description: t('forBusinesses.benefits.financial.description'),
      color: 'bg-primary/10 text-primary'
    },
    {
      icon: BarChart,
      title: t('forBusinesses.benefits.intelligence.title'),
      description: t('forBusinesses.benefits.intelligence.description'),
      color: 'bg-secondary/10 text-secondary'
    }
  ];

  const successMetrics = [
    { value: '150+', label: t('forBusinesses.metrics.partnerships') },
    { value: '$50M+', label: t('forBusinesses.metrics.contracts') },
    { value: '85%', label: t('forBusinesses.metrics.growth') },
    { value: '200+', label: t('forBusinesses.metrics.certified') }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-secondary">
        <div className="container relative py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-4xl text-center text-primary-foreground"
          >
            <h1 className="font-heading text-balance text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              {t('forBusinesses.hero.title')}
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/80 md:text-xl">
              {t('forBusinesses.hero.subtitle')}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
                <Link to="/opportunities?type=tender,jv">
                  {t('forBusinesses.cta.findOpportunities')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" asChild>
                <Link to="/programs">{t('forBusinesses.cta.explorePrograms')}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Success Metrics */}
      <section className="border-b bg-muted/30 py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4">{t('forBusinesses.metrics.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('forBusinesses.metrics.description')}</p>
          </motion.div>

          <div className="grid gap-8 text-center md:grid-cols-4">
            {successMetrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
              >
                <div className="font-heading text-4xl font-bold text-primary mb-2">{metric.value}</div>
                <div className="text-sm text-muted-foreground">{metric.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold mb-4">{t('forBusinesses.benefits.title')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t('forBusinesses.benefits.description')}</p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              >
                <Card className="h-full transition-shadow hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${benefit.color}`}>
                      <benefit.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contract Opportunities */}
      <section className="bg-muted/30 py-16">
        <div className="container">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold mb-2">{t('forBusinesses.opportunities.title')}</h2>
              <p className="text-muted-foreground">{t('forBusinesses.opportunities.description')}</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/opportunities">
                {t('forBusinesses.opportunities.viewAll')} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-40 w-full" />)}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {opportunities.map(opp => (
                <Link key={opp.id} to={`/opportunities/${opp.id}`}>
                  <Card className="h-full transition-shadow hover:shadow-lg">
                    <CardContent className="p-5 space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <TypeBadge type={opp.type} />
                        <SectorBadge sector={opp.sector} />
                        {opp.spa_linked && <Badge className="bg-spa-minerals/10 text-spa-minerals border-spa-minerals/30 text-xs">SPA</Badge>}
                      </div>
                      <h3 className="font-semibold line-clamp-2">{opp.title}</h3>
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Building2 className="h-3 w-3" />{opp.company?.name}
                      </p>
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />{opp.city ? `${opp.city}, ` : ''}{opp.country}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Connect with International Partners */}
      <section className="border-t py-16">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mx-auto max-w-3xl text-center"
          >
            <Handshake className="mx-auto mb-4 h-12 w-12 text-primary" />
            <h2 className="font-heading text-3xl font-bold mb-4">
              {t('forBusinesses.benefits.partnerships.title')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('companyProfile.types.us_company_desc')}
            </p>
            <Button size="lg" asChild>
              <Link to="/companies?type=us_company">
                {t('forCompanies.cta.findPartners')} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold mb-2">{t('forBusinesses.programs.title')}</h2>
              <p className="text-muted-foreground">{t('forBusinesses.programs.description')}</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/programs">
                {t('forBusinesses.programs.viewAll')} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid gap-6 md:grid-cols-2">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full" />)}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {programs.map(program => (
                <Card key={program.id} className="transition-shadow hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold line-clamp-2 flex-1">{program.title}</h3>
                      {program.deadline && (
                        <Badge variant="secondary" className="ml-3 shrink-0">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(program.deadline).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm line-clamp-3 mb-3">{program.description}</p>
                    {program.spa_pillar && (
                      <Badge variant="outline" className="text-xs">
                        {t(`spaPillars.${program.spa_pillar}`)}
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="container py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="rounded-2xl bg-gradient-to-r from-primary to-secondary p-8 text-center text-white md:p-12"
        >
          <h2 className="font-heading text-3xl font-bold md:text-4xl mb-4">
            {t('forBusinesses.cta.title')}
          </h2>
          <p className="mx-auto max-w-2xl text-white/80 mb-8">
            {t('forBusinesses.cta.description')}
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
              <Link to="/auth?mode=signup">{t('forBusinesses.cta.joinNetwork')}</Link>
            </Button>
            <Button size="lg" variant="link" className="text-white" asChild>
              <Link to="/companies">{t('forBusinesses.cta.browsePlatform')}</Link>
            </Button>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
};

export default ForBusinesses;